import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { History, Filter, Calendar as CalendarIcon, User, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import api from "@/lib/api";
import BottomNav, { Sidebar } from "@/components/Navigation";
import { toast } from "sonner";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "record_partial", label: "Record Partial" },
  { value: "refund_full", label: "Full Refund" },
  { value: "refund_partial", label: "Partial Refund" },
];

const ACTION_STYLES = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  record_partial: "bg-violet-50 text-violet-700 border-violet-200",
  refund_full: "bg-red-50 text-red-700 border-red-200",
  refund_partial: "bg-amber-50 text-amber-700 border-amber-200",
};

const AuditLog = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const [logs, setLogs] = useState([]);
  const [studios, setStudios] = useState([]);
  const [filters, setFilters] = useState({ action: "", studio_id: "", actor_user_id: "", limit: 200 });
  const [loading, setLoading] = useState(false);
  const [actors, setActors] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.studio_id) params.studio_id = filters.studio_id;
      if (filters.actor_user_id) params.actor_user_id = filters.actor_user_id;
      params.limit = filters.limit;
      const res = await api.get("/audit/payments", { params });
      setLogs(res.data?.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/studios").then((r) => setStudios(r.data?.studios || []));
    Promise.all([
      api.get("/users", { params: { role: "admin" } }),
      api.get("/users", { params: { role: "instructor" } }),
    ]).then(([a, i]) => {
      setActors([
        ...(a.data?.users || []),
        ...(i.data?.users || []),
      ]);
    });
    fetchData();
  }, []);

  const studioName = (sid) => studios.find((s) => s.studio_id === sid)?.name || "—";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pb-24 md:pb-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 12%, white)" }}>
              <History className="w-6 h-6" style={{ color: "var(--brand-primary)" }} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Payment Audit Log</h1>
              <p className="text-sm text-slate-500 mt-0.5">Every payment create, edit, record, and refund — fully traceable.</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Action</Label>
                <select
                  data-testid="audit-filter-action"
                  value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  {ACTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Studio</Label>
                <select
                  data-testid="audit-filter-studio"
                  value={filters.studio_id}
                  onChange={(e) => setFilters({ ...filters, studio_id: e.target.value })}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">All studios</option>
                  {studios.map((s) => (
                    <option key={s.studio_id} value={s.studio_id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Actor</Label>
                <select
                  data-testid="audit-filter-actor"
                  value={filters.actor_user_id}
                  onChange={(e) => setFilters({ ...filters, actor_user_id: e.target.value })}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Anyone</option>
                  {actors.map((a) => (
                    <option key={a.user_id} value={a.user_id}>{a.name} ({a.role})</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="audit-apply-filter"
                  onClick={fetchData}
                  className="w-full h-10 rounded-xl text-white"
                  style={{ backgroundColor: "var(--brand-primary)" }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>

          {/* Log entries */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Actor</th>
                  <th className="text-left p-3">Studio</th>
                  <th className="text-left p-3">Payment</th>
                  <th className="text-left p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">Loading...</td></tr>
                )}
                {!loading && logs.length === 0 && (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">No audit entries match your filters.</td></tr>
                )}
                {!loading && logs.map((log) => (
                  <tr key={log.audit_id} data-testid={`audit-row-${log.audit_id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                      {fmt(log.timestamp, "dd MMM yyyy", default_timezone)}
                      <div className="text-slate-400">{fmt(log.timestamp, "hh:mm a", default_timezone)}</div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${ACTION_STYLES[log.action] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {(log.action || "").replace("_", " ")}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-slate-800">{log.actor_name}</div>
                      <div className="text-xs text-slate-400 capitalize">{log.actor_role?.replace("_", " ")}</div>
                    </td>
                    <td className="p-3 text-xs text-slate-600">{studioName(log.studio_id)}</td>
                    <td className="p-3 font-mono text-xs text-slate-600">{(log.payment_id || "").substring(0, 8)}…</td>
                    <td className="p-3 text-xs text-slate-600">
                      {log.extra?.amount !== undefined && (
                        <div>Amount: ₹{log.extra.amount}</div>
                      )}
                      {log.extra?.reason && (
                        <div className="italic text-slate-500">"{log.extra.reason}"</div>
                      )}
                      {log.extra?.transaction && (
                        <div>₹{log.extra.transaction.amount} via {log.extra.transaction.method}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default AuditLog;
